import React from 'react';
import { DollarSign, Shield, Lock, Check, X } from 'lucide-react';

export function CompareTable() {
  return (
    <div className="flex flex-col gap-8">
      {/* Section 1: Cost Comparison */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-emerald-500" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">Cost Comparison</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          Most AI resume platforms use an expensive, recurring subscription model. ResumePro offers a generous free tier and true pay-as-you-go top-ups.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-rose-200 dark:border-rose-900/30 rounded-xl p-5 bg-rose-50 dark:bg-rose-950/20">
            <h4 className="font-semibold text-rose-600 dark:text-rose-400 mb-4 uppercase text-xs tracking-wider">Other SaaS Platforms</h4>
            <ul className="space-y-4 mb-5">
              <li className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                <X className="w-5 h-5 text-rose-500 shrink-0 mt-0.5 bg-rose-100 dark:bg-rose-500/20 rounded-full p-0.5" />
                <div>
                  <strong>AI Apply / Resume Worded</strong>
                  <div className="text-slate-500 dark:text-slate-400 mt-0.5">~$39 - $49/month</div>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300">
                <X className="w-5 h-5 text-rose-500 shrink-0 mt-0.5 bg-rose-100 dark:bg-rose-500/20 rounded-full p-0.5" />
                <div>
                  <strong>Teal / Rezi AI (Pro)</strong>
                  <div className="text-slate-500 dark:text-slate-400 mt-0.5">~$29/month</div>
                </div>
              </li>
              <li className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300 pt-2 border-t border-slate-200 dark:border-slate-700">
                <X className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                <span>Strict limits on AI generations & recurring charges even when not applying</span>
              </li>
            </ul>
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="text-sm text-slate-500">Typical cost for a 3-month job hunt:</div>
              <div className="text-xl font-bold text-slate-900 dark:text-white">~$75 - $110</div>
            </div>
          </div>

          <div className="border border-emerald-500/30 dark:border-emerald-500/30 rounded-xl p-5 bg-emerald-50/50 dark:bg-emerald-500/5 relative dark:shadow-sm">
            <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
              <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">Pay As You Go</span>
            </div>
            <h4 className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2 uppercase text-xs tracking-wider">ResumePro</h4>
            <ul className="space-y-3 mb-4">
              <li className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <span><strong>Free Tier:</strong> 20 free credits when you first sign up.</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <span><strong>No Subscriptions:</strong> Only pay when you run out of free limits.</span>
              </li>
               <li className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                <span><strong>$5.00 for 100 Credits:</strong> Fractional cost compared to competitors.</span>
              </li>
            </ul>
            <div className="pt-4 border-t border-emerald-200 dark:border-emerald-800/50">
              <div className="text-sm text-emerald-700/80 dark:text-emerald-400/80">Estimated cost for 100 job apps:</div>
              <div className="text-xl font-bold text-emerald-700 dark:text-emerald-400">$0.00 - $5.00 <span className="text-sm font-normal opacity-75">(Depending on free usage)</span></div>
            </div>
          </div>
        </div>
      </section>

      <div className="h-px bg-slate-200 dark:bg-slate-800 w-full" />

      {/* Section 2: Privacy Comparison */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-indigo-500" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">Zero-Log Data Privacy</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          Resumes contain highly sensitive personal identifiable information (PII). ResumePro is built to respect your data.
        </p>

        <div className="space-y-3">
          <div className="flex gap-3 p-3 rounded-xl border border-rose-100 dark:border-rose-900/30 bg-rose-50/50 dark:bg-rose-950/20 dark:shadow-sm">
            <div className="bg-rose-100/50 dark:bg-rose-500/10 p-2 rounded-lg h-fit shrink-0">
              <X className="w-5 h-5 text-rose-500" />
            </div>
            <div>
              <h5 className="font-medium text-rose-900 dark:text-rose-200 text-sm">Other SaaS Platforms</h5>
              <p className="text-xs text-rose-800/80 dark:text-rose-300/80 mt-1 leading-relaxed">
                Store your name, phone, address, work history, and API interactions in their standard cloud databases. They own your data and could potentially sell or use it to train their models depending on their ToS.
              </p>
            </div>
          </div>

          <div className="flex gap-3 p-3 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/30 dark:bg-indigo-500/5 dark:shadow-sm">
            <div className="bg-indigo-100 dark:bg-indigo-500/20 p-2 rounded-lg h-fit shrink-0">
              <Lock className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h5 className="font-medium text-indigo-900 dark:text-indigo-300 text-sm">ResumePro Architecture</h5>
              <p className="text-xs text-slate-700 dark:text-slate-300 mt-1 leading-relaxed">
                Your resume data stays exactly where it belongs: <strong>on your device</strong>. Our servers act as a secure, private bridge to Google's AI, and we never store or log your actual resumes or job descriptions. The only things we save are your email address and credit limits. Plus, Google's enterprise API explicitly guarantees that your data is <strong>never used</strong> to train their AI models.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
